import { useState, useEffect, useCallback, useRef } from 'react';
import { getAuthToken } from '~/contexts/AuthContext';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8787';

interface PushNotificationState {
  supported: boolean;
  permission: NotificationPermission;
  subscribed: boolean;
  loading: boolean;
  error: string | null;
}

/**
 * Convert a base64 string to Uint8Array for applicationServerKey
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Hook for managing push notification subscriptions
 */
export function usePushNotifications() {
  const [state, setState] = useState<PushNotificationState>({
    supported: false,
    permission: 'default',
    subscribed: false,
    loading: true,
    error: null,
  });
  const autoResubscribeRef = useRef(false);
  const subscribeRef = useRef<(() => Promise<boolean>) | null>(null);

  // Check initial state — auto-resubscribe if user opted in but local subscription is gone
  useEffect(() => {
    const checkState = async () => {
      // Check if push notifications are supported
      const supported =
        'serviceWorker' in navigator &&
        'PushManager' in window &&
        'Notification' in window;

      if (!supported) {
        setState({
          supported: false,
          permission: 'denied',
          subscribed: false,
          loading: false,
          error: 'Push notifications not supported in this browser',
        });
        return;
      }

      const permission = Notification.permission;

      // Check if already subscribed locally
      let subscribed = false;
      try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        subscribed = !!subscription;
      } catch {
        // Ignore errors checking subscription
      }

      if (!subscribed && permission === 'granted') {
        // Local subscription is gone but user previously granted permission.
        // Check backend preference to see if they opted in.
        try {
          const token = getAuthToken();
          if (token) {
            const res = await fetch(`${API_BASE}/api/push/preference`, {
              credentials: 'include',
              headers: { Authorization: `Bearer ${token}` },
            });
            if (res.ok) {
              const { pushEnabled } = await res.json();
              if (pushEnabled) {
                // User opted in but subscription was lost (SW update / 410 cleanup).
                // Re-subscribe transparently.
                console.log('[Push] Auto-resubscribing — subscription lost after SW update');
                setState({ supported, permission, subscribed: false, loading: true, error: null });
                // Trigger subscribe after state is set (will be called below via ref)
                autoResubscribeRef.current = true;
              }
            }
          }
        } catch {
          // Non-critical — fall through to normal state
        }
      }

      if (!autoResubscribeRef.current) {
        setState({
          supported,
          permission,
          subscribed,
          loading: false,
          error: null,
        });
      }
    };

    checkState();
  }, []);

  /**
   * Subscribe to push notifications
   */
  const subscribe = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      // Request notification permission
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setState((prev) => ({
          ...prev,
          permission,
          loading: false,
          error: 'Notification permission denied',
        }));
        return false;
      }

      // Get VAPID public key from server
      const token = getAuthToken();
      const authHeaders: Record<string, string> = {};
      if (token) {
        authHeaders['Authorization'] = `Bearer ${token}`;
      }

      const vapidResponse = await fetch(`${API_BASE}/api/push/vapid-public-key`, {
        credentials: 'include',
        headers: authHeaders,
      });
      if (!vapidResponse.ok) {
        const err = await vapidResponse.json().catch(() => null);
        throw new Error(err?.error || `Failed to get VAPID public key (${vapidResponse.status})`);
      }
      const { publicKey } = await vapidResponse.json();

      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;

      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey).buffer as ArrayBuffer,
      });

      // Send subscription to backend
      const subscribeResponse = await fetch(`${API_BASE}/api/push/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        credentials: 'include',
        body: JSON.stringify(subscription.toJSON()),
      });

      if (!subscribeResponse.ok) {
        const err = await subscribeResponse.json().catch(() => null);
        throw new Error(err?.error || `Failed to save subscription on server (${subscribeResponse.status})`);
      }

      // Send config to service worker so it can re-subscribe autonomously
      // if the browser invalidates the endpoint (e.g., after SW update)
      if (navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: 'PUSH_CONFIG',
          config: {
            apiBase: API_BASE,
            vapidPublicKey: publicKey,
            authToken: token,
          },
        });
      }

      setState({
        supported: true,
        permission: 'granted',
        subscribed: true,
        loading: false,
        error: null,
      });

      return true;
    } catch (error) {
      console.error('Push subscribe error:', error);
      let message = error instanceof Error ? error.message : 'Failed to subscribe';
      if (error instanceof DOMException && error.name === 'AbortError') {
        message =
          'Push registration blocked by your browser. If using Brave, go to brave://settings/privacy and enable "Use Google services for push messaging". Otherwise, try Chrome.';
      }
      setState((prev) => ({
        ...prev,
        loading: false,
        error: message,
      }));
      return false;
    }
  }, []);

  // Keep ref in sync so the init effect can call subscribe
  subscribeRef.current = subscribe;

  // Auto-resubscribe effect — fires when checkState sets the flag
  useEffect(() => {
    if (autoResubscribeRef.current) {
      autoResubscribeRef.current = false;
      subscribeRef.current?.();
    }
  });

  /**
   * Unsubscribe from push notifications
   */
  const unsubscribe = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        // Unsubscribe locally
        await subscription.unsubscribe();

        // Remove from backend
        const token = getAuthToken();
        const authHeaders: Record<string, string> = { 'Content-Type': 'application/json' };
        if (token) {
          authHeaders['Authorization'] = `Bearer ${token}`;
        }
        await fetch(`${API_BASE}/api/push/subscribe`, {
          method: 'DELETE',
          headers: authHeaders,
          credentials: 'include',
          body: JSON.stringify({ endpoint: subscription.endpoint }),
        });
      }

      setState((prev) => ({
        ...prev,
        subscribed: false,
        loading: false,
        error: null,
      }));

      return true;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to unsubscribe';
      setState((prev) => ({
        ...prev,
        loading: false,
        error: message,
      }));
      return false;
    }
  }, []);

  return {
    ...state,
    subscribe,
    unsubscribe,
  };
}
